import { ChangeEvent, DragEvent, RefObject, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatFileSize } from './uploadAreaUtils';

type UseUploadAreaParams = Readonly<{
  acceptedExtensions: string[];
  isDisabled: boolean;
  maxSizeBytes: number;
  onFilesSelected: (files: File[]) => void;
}>;

type UseUploadAreaResult = Readonly<{
  acceptAttribute: string;
  allowedFormatsLabel: string;
  allowedFormatsText: string;
  browseText: string;
  dragAnnouncementText: string;
  dropTitleText: string;
  fileInputRef: RefObject<HTMLInputElement>;
  handleClickSelect: () => void;
  handleDragEnter: (event: DragEvent) => void;
  handleDragLeave: (event: DragEvent) => void;
  handleDragOver: (event: DragEvent) => void;
  handleDrop: (event: DragEvent) => void;
  handleFileInputChange: (event: ChangeEvent<HTMLInputElement>) => void;
  isDragOver: boolean;
  maxFileSizeLabel: string;
  maxFileSizeText: string;
}>;

function handleDragOver(event: DragEvent): void {
  event.preventDefault();
}

function useUploadArea({
  acceptedExtensions,
  isDisabled,
  maxSizeBytes,
  onFilesSelected,
}: UseUploadAreaParams): UseUploadAreaResult {
  const { t } = useTranslation(['actions', 'common', 'content']);
  const [isDragOver, setIsDragOver] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef<number>(0);

  const normalizedExtensions = acceptedExtensions.map((ext) => ext.toLowerCase());
  const acceptAttribute = normalizedExtensions.map((ext) => `.${ext}`).join(',');
  const allowedFormatsLabel = normalizedExtensions.map((ext) => ext.toUpperCase()).join(', ');

  function handleDragEnter(event: DragEvent): void {
    event.preventDefault();
    if (isDisabled) return;
    dragCounterRef.current += 1;
    setIsDragOver(true);
  }

  function handleDragLeave(event: DragEvent): void {
    event.preventDefault();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) setIsDragOver(false);
  }

  function handleDrop(event: DragEvent): void {
    event.preventDefault();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    if (isDisabled) return;
    const files = Array.from(event.dataTransfer.files);
    if (files.length > 0) onFilesSelected(files);
  }

  function handleClickSelect(): void {
    if (isDisabled) return;
    fileInputRef.current?.click();
  }

  function handleFileInputChange(event: ChangeEvent<HTMLInputElement>): void {
    const files = Array.from(event.target.files ?? []);
    if (files.length > 0) onFilesSelected(files);
    event.target.value = '';
  }

  const maxFileSizeLabel = formatFileSize(maxSizeBytes);
  const dropTitleText = t('content:Drop your file here or');
  const browseText = t('content:Browse');
  const maxFileSizeText = t('actions:Max file size');
  const allowedFormatsText = t('actions:Allowed formats');
  const dragAnnouncementText = t('content:Drop file to upload');

  return {
    acceptAttribute,
    allowedFormatsLabel,
    allowedFormatsText,
    browseText,
    dragAnnouncementText,
    dropTitleText,
    fileInputRef,
    handleClickSelect,
    handleDragEnter,
    handleDragLeave,
    handleDragOver,
    handleDrop,
    handleFileInputChange,
    isDragOver,
    maxFileSizeLabel,
    maxFileSizeText,
  };
}

export default useUploadArea;
